import { Box, Button, Stack, Typography } from '@mui/material'
import type { FC } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { COLORS, LinkUnstyled, unit } from '@hcr/ui'
import { filter, flow, isNotNull, isNotUndefined, isSame, unique } from '@hcr/utils'

import { useHoliday } from '../../contexts'
import { useDestinationDetailsQuery, useTicketsCount } from '../../hooks'
import { Path } from '../../models'
import { createDestinationDetailsContent, createTicketsTotalCount, to } from '../../utils'
import { TicketsInfo } from '../common'

import { DestinationDrawer, DestinationInfo, LayoutHome } from './common'
import { HomeDestinationLoading } from './HomeDestinationLoading'
import { HomeEmpty } from './HomeEmpty'

const destinationDetailsContent = createDestinationDetailsContent({ slidesMargin: unit(3) })

export const HomeDestination: FC = () => {
  const { t } = useTranslation()
  const {
    data: { destination },
    ...holiday
  } = useHoliday()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const openDrawer = () => setIsDrawerOpen(true)
  const closeDrawer = () => setIsDrawerOpen(false)

  const destinationDetails = useDestinationDetailsQuery(
    { destinationId: String(destination?.destinationId) },
    { enabled: isNotUndefined(destination) }
  )

  const ticketsCount = useTicketsCount()

  const resortsIds = useDestinationDetailsQuery(
    { destinationId: String(destination?.destinationId) },
    {
      select: flow(
        destinationDetails => [destinationDetails.hotelId, destinationDetails.mokkiId],
        filter(isNotNull),
        unique(isSame)
      ),
      enabled: isNotUndefined(destination),
      retryOnMount: false,
    }
  )

  if (holiday.isError || destinationDetails.isError) {
    return <HomeEmpty />
  }

  if (holiday.isSuccess && destinationDetails.isSuccess && resortsIds.isSuccess) {
    const resortId = resortsIds.data[0]
    const totalTickets = createTicketsTotalCount(ticketsCount, resortId)

    return (
      <>
        <LayoutHome
          imageUrl={destinationDetails.data.imageUrl}
          header={
            <Stack gap={unit(4)} sx={{ backgroundColor: COLORS.burgundi[500], color: COLORS.white, padding: unit(3) }}>
              <Stack padding={unit(2)} justifyItems='center' alignItems='center'>
                <Typography visibility='hidden' component='h1' sx={{ height: unit(0) }} fontWeight='initial'>
                  {destinationDetails.data.name}
                </Typography>
                <Box
                  component='img'
                  alt={destinationDetails.data.name}
                  src={destinationDetails.data.logoUrl}
                  width='auto'
                  height={unit(9)}
                />
              </Stack>

              <Stack gap={unit(4)} padding={unit(5)} bgcolor={COLORS.red.dark} color={COLORS.white}>
                {isNotNull(ticketsCount) && totalTickets > 0 ? (
                  <TicketsInfo
                    title={
                      <Typography component='h2' variant='headlineS' fontWeight='initial'>
                        {t('booking.no-accommodation-booked')}
                      </Typography>
                    }
                    totalTickets={totalTickets}
                    cta={
                      <Button component={LinkUnstyled} to={to(Path.Booking)} variant='outlined' color='white'>
                        {t('home.open')}
                      </Button>
                    }
                  />
                ) : (
                  <DestinationInfo
                    description={destinationDetails.data.description}
                    destinationId={String(destination?.destinationId)}
                    openDrawer={openDrawer}
                  />
                )}
              </Stack>
            </Stack>
          }
          content={destinationDetails.data.content.map(destinationDetailsContent)}
        />
        <DestinationDrawer
          description={destinationDetails.data.description}
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          resortName={destinationDetails.data.name}
          title={destinationDetails.data.title}
        />
      </>
    )
  }

  return <HomeDestinationLoading />
}
